import React, { Component, createRef, RefObject } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import _isEqual from 'lodash/isEqual';

import { locale } from '@trqk/utils';

import { BulletKey, QuarterlyRunRateLegend } from './components';
import { mapSeriesListToLegendItems } from './utils';
import { bulletKeyHeight, squareBulletWidth } from './constants';

interface QuarterlyRunRateProps {
    /**
     * Array of {@link QRRChartData} objects
     */
    data: QRRChartData[];
    /**
     * The subscription_royalty_capacity in the user's current payment plan, to be displayed in the
     * QuarterlyRunRateLegend
     */
    royaltyCapacity: Maybe<number>;
    /**
     * Object where each key is an entity ID and its value is a {@link QRRSeriesConfig} object
     */
    seriesConfigs: QRRSeriesConfigs;
    /**
     * Height for the chart in pixels
     */
    height: number;
    /**
     * A number formatting string - see {@link https://www.amcharts.com/docs/v4/concepts/formatters/formatting-numbers/ | amCharts docs}
     */
    numberFormat?: string;
    /**
     * optional callback invoked when chart is ready
     */
    onChartReady?(): void;
    /**
     * When true, the word "Unlimited" will be used as the label for royalty capacity in the legend
     * summary.
     * @defaultValue false
     */
    unlimitedRoyalties?: boolean;
}

interface QuarterlyRunRateState {
    legendItems: QRRLegendTableItem[];
    refresh: number;
}

/**
 * A line chart showing multiple lines, one for each entity's statements, where the y-axis is the
 * total revenue for the statement and the x-axis is the Date representing that statement's end
 * date.
 * @param props - see {@link QuarterlyRunRateProps}
 */
export class QuarterlyRunRate extends Component<
    QuarterlyRunRateProps,
    QuarterlyRunRateState
> {
    private chart: am4charts.XYChart | null = null;

    private divRef: RefObject<HTMLDivElement> | null = null;

    constructor(props: QuarterlyRunRateProps) {
        super(props);

        this.divRef = createRef();
        this.addLineSeries = this.addLineSeries.bind(this);
        this.state = { legendItems: [], refresh: 0 };
    }

    componentDidMount(): void {
        if (this.divRef?.current) {
            const { data, numberFormat, onChartReady } = this.props;
            const chart = am4core.create(
                this.divRef.current,
                am4charts.XYChart,
            );
            const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
            const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());

            if (numberFormat) {
                chart.numberFormatter.numberFormat = numberFormat;
            }

            chart.fontSize = 12;
            chart.padding(10, 30, 10, 20);
            chart.numberFormatter.bigNumberPrefixes = [
                { number: 1e3, suffix: 'K' },
                { number: 1e6, suffix: 'M' },
                { number: 1e9, suffix: 'B' },
                { number: 1e12, suffix: 'T' },
                { number: 1e15, suffix: 'Q' },
            ];
            chart.numberFormatter.smallNumberThreshold = 1e-12;
            chart.numberFormatter.smallNumberPrefixes = [
                { number: 1e-12, suffix: '' },
            ];

            chart.minZoomCount = data.length;
            chart.maxZoomCount = data.length;
            chart.zoomOutButton.disabled = true;

            dateAxis.renderer.minGridDistance = 40;
            dateAxis.cursorTooltipEnabled = true;
            dateAxis.startLocation = 0;
            dateAxis.endLocation = 0;
            dateAxis.baseInterval = {
                timeUnit: 'month',
                count: 3,
            };
            dateAxis.gridIntervals.setAll([
                { timeUnit: 'month', count: 3 },
                { timeUnit: 'year', count: 1 },
                { timeUnit: 'year', count: 2 },
                { timeUnit: 'year', count: 5 },
                { timeUnit: 'year', count: 10 },
                { timeUnit: 'year', count: 50 },
                { timeUnit: 'year', count: 100 },
                { timeUnit: 'year', count: 500 },
                { timeUnit: 'year', count: 1000 },
            ]);
            dateAxis.dateFormats.setKey('month', 'Qq');
            dateAxis.periodChangeDateFormats.setKey('month', 'yyyy Qq');
            dateAxis.extraMax = 0.05;
            dateAxis.extraMin = 0.05;

            valueAxis.cursorTooltipEnabled = true;
            valueAxis.renderer.minGridDistance = 40;
            valueAxis.extraMin = 0.01;
            valueAxis.extraMax = 0.01;

            chart.data = data;

            chart.events.on('ready', () => {
                if (onChartReady) {
                    onChartReady();
                }

                this.setState({
                    legendItems: mapSeriesListToLegendItems(
                        chart?.series ?? null,
                    ),
                    refresh: Date.now(),
                });
            });

            this.chart = chart;

            this.addLineSeries();
        }
    }

    componentDidUpdate(prevProps: QuarterlyRunRateProps): void {
        const { data } = this.props;

        if (!_isEqual(prevProps.data, data)) {
            if (this.chart !== null) {
                this.chart.data = data;
            }
        }
    }

    componentWillUnmount(): void {
        if (this.chart) {
            this.chart.dispose();
        }
    }

    addLineSeries(): void {
        const {
            chart,
            props: { seriesConfigs },
        } = this;

        if (chart === null || !chart.data) {
            return;
        }

        chart.series.clear();

        Object.values(seriesConfigs).forEach(
            ({ entityName, dataKeys }: QRRSeriesConfig): void => {
                const lineSeries = chart.series.push(
                    new am4charts.LineSeries(),
                );
                const bullet1 = lineSeries.bullets.push(
                    new am4charts.CircleBullet(),
                );

                bullet1.circle.radius = 4;
                bullet1.circle.tooltipText = `[bold]{${dataKeys.entityName}}[/] {displayDate}: {${dataKeys.earnings}}`;
                bullet1.circle.propertyFields.hidden =
                    dataKeys.hideCircleBullet;

                const bullet2 = lineSeries.bullets.push(new am4charts.Bullet());

                const square = bullet2.createChild(am4core.Rectangle);

                square.width = squareBulletWidth;
                square.height = squareBulletWidth;
                square.horizontalCenter = 'middle';
                square.verticalCenter = 'middle';
                square.tooltipText = `[bold]{${dataKeys.entityName}}[/] {displayDate}: {${dataKeys.earnings}}`;
                square.propertyFields.hidden = dataKeys.hideSquareBullet;

                lineSeries.minBulletDistance = 20;
                lineSeries.name = entityName;
                lineSeries.dataFields.valueY = dataKeys.earnings;
                lineSeries.dataFields.dateX = 'date';
                lineSeries.dataItems.template.locations.dateX = 0;
                lineSeries.strokeWidth = 2;
                lineSeries.setStateOnChildren = true;

                if (lineSeries.tooltip) {
                    lineSeries.tooltip.pointerOrientation = 'vertical';
                }
            },
        );
    }

    render(): JSX.Element {
        const {
            props: { height, royaltyCapacity, unlimitedRoyalties },
            state: { legendItems, refresh },
        } = this;

        const chartHeight = height - bulletKeyHeight;

        return (
            <div className="flex ml-3 mr-8">
                <div className="w-full">
                    <div
                        ref={this.divRef}
                        className="w-full"
                        style={{ height: chartHeight }}
                    />
                    <p className="w-full text-center">
                        {
                            locale.enUS.MANAGE_STATEMENTS.TOTAL_ROYALTIES
                                .DISTRIBUTION_PERIOD
                        }
                    </p>
                    <BulletKey />
                </div>
                <div className="flex-none">
                    <QuarterlyRunRateLegend
                        height={height}
                        items={legendItems}
                        refresh={refresh}
                        royaltyCapacity={royaltyCapacity}
                        unlimitedRoyalties={unlimitedRoyalties ?? false}
                    />
                </div>
            </div>
        );
    }
}
